import { Grid, Skeleton, Stack, TableCell, Typography } from "@mui/material";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient } from "react-query";
import { generateColorGradient, handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import {
  mandayPerVendor,
  mandayPerVendorAndLocation,
  mandayPerVendorAndModality,
  topicsPerVendor,
  workshopCompletionRate,
  workshopCountPerVendor,
} from "framework/charts";
import { IChartView } from "models/app";
import { CHARTS } from "constants/enums";
import ChartView from "components/UI/ChartView/ChartView";
import Filters from "./partials/filters";
import { Bar } from "react-chartjs-2";
import DialogModal from "components/UI/DialogModal";
import {
  WorkshopChartsCellsCount,
  WorkshopChartsCellsCount1,
} from "framework/workshops";
import List from "components/UI/List";
import { getPermission, getUserData } from "helpers";
import { IPermission } from "models/permissions";
import EmptyContent from "components/EmptyContent";
import BlockIcon from "@mui/icons-material/Block";

interface Props {
  feature: Feature;
}

const AnalyticsVendor: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const [permission, setPermission] = useState<IPermission | null>(null);
  const { t, i18n } = useTranslation();
  const [data1, setData1] = useState<any[]>([]);
  const [data2, setData2] = useState<any[]>([]);
  const [data3, setData3] = useState<any[]>([]);
  const [data3Change, setData3Change] = useState<any[] | null>(null);
  const [data4, setData4] = useState<any[]>([]);
  const [data5, setData5] = useState<any[]>([]);
  const [data6, setData6] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<any[] | null>(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState<any[] | null>(null);
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const [dateFrom, setDateFrom] = useState<any>(new Date(year, month, 1));
  const [dateTo, setDateTo] = useState<any>(new Date(year, month + 1, 0));
  const user = getUserData();
  const { colors: colors1 } = user;
  const colors: string[] = generateColorGradient(colors1?.chartMainColor);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let promise1 = queryClient.fetchQuery(
        "total manday per vendor and modality",
        () =>
          mandayPerVendorAndModality(
            `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
          )
      );
      let promise2 = queryClient.fetchQuery(
        "get Workshops Completion Rate",
        () =>
          workshopCompletionRate(
            `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
          )
      );

      let promise3 = queryClient.fetchQuery(
        "get  total manday per vendor",
        () =>
          mandayPerVendor(
            `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
          )
      );

      let promise4 = queryClient.fetchQuery(
        "Get Workshops Count Per Vendor",
        () =>
          workshopCountPerVendor(
            `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
          )
      );

      let promise5 = queryClient.fetchQuery("get Manday Per Vendor", () =>
        mandayPerVendorAndLocation(
          `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
        )
      );

      let promise6 = queryClient.fetchQuery("get topics per vendor", () =>
        topicsPerVendor(
          `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
        )
      );

      const [response1, response2, response3, response4, response5, response6] =
        await Promise.all([
          promise1,
          promise2,
          promise3,

          promise4,
          promise5,
          promise6,
        ]);
      setData1(response1);
      setData2(response2);
      setData3(response3);
      setData4(response4);
      setData5(response5);
      setData6(response6);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(21);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, [dateFrom, dateTo]);

  const topics = [...new Set(data6.map((item) => item.topicName))];
  const vendors1 = [...new Set(data6.map((item) => item.vendorName))];

  const chartData4: IChartView = {
    data: {
      Labels: data4.map((item: any) => item?.vendorName),
      Values: data4.map((item: any) => item?.workshopCount),
      Ids: data4.map((item: any) => item?.workshops),
    },
    label: t("T_dashboard_vendor_2"),
    type: CHARTS.BAR,
    backgroundColor: colors1?.chartMainColor || "#513cc7",

    borderColor: "#513cc7",

    Id: "mandayPerVendor",
  };

  const chartData3: IChartView = {
    data: {
      Labels: data3.map((item: any) => item?.vendorName),
      Values: data3.map((item: any) => item?.totalMandays),
      Ids: data3.map((item: any) => item?.trainerMandays),
    },
    label: t("T_dashboard_vendor_3"),
    type: CHARTS.BAR,
    backgroundColor: colors1?.chartMainColor || "#513cc7",

    borderColor: "#513cc7",

    Id: "mandayPerVendor",
  };
  const chartData3Change: IChartView = {
    data: {
      Labels: data3Change!?.map((item: any) => item?.trainerName),
      Values: data3Change!?.map((item: any) => item?.totalMandays),
    },
    label: t("T_dashboard_vendor_3"),
    type: CHARTS.BAR,
    backgroundColor: colors1?.chartMainColor || "#513cc7",

    borderColor: "#513cc7",

    Id: "mandayPerVendor",
  };

  const chartData2: IChartView = {
    data: {
      Labels: data2.map((item: any) => item?.vendorName),
      Values: data2.map((item: any) => item?.completionPercentage),
      Ids: data2.map((item: any) => item?.workshopsCompletionRate),
    },
    label: t("T_dashboard_vendor_4"),
    type: CHARTS.BAR,
    backgroundColor: colors1?.chartMainColor || "#513cc7",

    borderColor: "#513cc7",

    Id: "workshopsCompletionRate",
  };

  const options1: any = {
    indexAxis: "y" as const,
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          font: {
            size: 17,
            weight: "bold",
          },
        },
      },
      datalabels: {
        display: true,
        color: "white",
        font: {
          size: 20,
        },
      },
    },

    scales: {
      x: {
        stacked: true,
        ticks: {
          font: {
            size: 14,
            weight: "bold",
          },
        },
      },
      y: {
        stacked: true,
        ticks: {
          font: {
            size: 14,
            weight: "bold",
          },
        },
      },
    },
    elements: {
      bar: {
        borderRadius: 10,
      },
    },
  };

  const data = {
    labels: data1.map((item: any) => item?.vendorName),
    datasets: [
      {
        label: t("T_dashboard_vendor_7"),
        data: data1.map((item: any) => item?.virtualManDays),
        backgroundColor: generateColorGradient(
          colors1?.chartMainColor,
          2,
          2,
          2
        )[0],
      },
      {
        label: t("T_dashboard_vendor_8"),
        data: data1.map((item: any) => item?.physicalManDays),
        backgroundColor: generateColorGradient(
          colors1?.chartMainColor,
          2,
          1,
          1
        ),
      },
    ],
  };

  const vendors = [...new Set(data5.map((item) => item.vendor))];
  const cities = [...new Set(data5.map((item) => item.city))];

  const chartData = {
    labels: cities,
    datasets: vendors.map((vendor, index) => ({
      label: vendor,
      data: cities.map((city) => {
        const vendorData = data5.find(
          (item) => item.vendor === vendor && item.city === city
        );
        return vendorData ? vendorData.totalManDays : 0;
      }),
      backgroundColor: generateColorGradient(
        colors1?.chartMainColor,
        vendors.length,
        2,
        2
      )[index], // Example colors, add more if needed
    })),
  };

  const chartData6New = {
    labels: topics,
    datasets: vendors1.map((vendor, index) => ({
      label: vendor,
      data: topics.map((topic) => {
        const vendorData = data6.find(
          (item) => item.vendorName === vendor && item.topicName === topic
        );
        return vendorData ? vendorData.topicCount : 0;
      }),
      backgroundColor: generateColorGradient(
        colors1?.chartMainColor,
        topics.length,
        2,
        2
      )[index],
    })),
  };

  const handleShowData = (data: any) => {
    setSelectedId(data);
  };

  const handleShowDataCount = (data: any) => {
    setSelectedWorkshop(data);
  };

  const handleShowDataCount1 = (data: any) => {
    setData3Change(data);
  };

  const handleBackButtonClick = () => {
    setData3Change(null);
  };

  const getBodyCells1 = (row: any) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "workshopTitle")
        return {
          id: "workshopTitle",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.workshopTitle : row.workshopTitle}
              </Typography>
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };
  const getBodyCells2 = (row: any) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "workshopTitle")
        return {
          id: "workshopTitle",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.workshopTitle : row.workshopTitle}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "completionPercentage")
        return {
          id: "completionPercentage",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en"
                  ? row.completionPercentage
                  : row.completionPercentage}
              </Typography>
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };

  if (loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  if (!permission?.hasReadPermission)
    return (
      <EmptyContent
        icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
        header={t("T_no_permissions")}
        message={t("T_no_permissions_message")}
      />
    );

  return (
    <Stack spacing={2} p={2}>
      <Grid
        container
        justifyContent={"space-between"}
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Stack direction={"row"} spacing={1}>
          <Filters
            onChangeFrom={(e) => setDateFrom(e)}
            onChangeTo={(e) => setDateTo(e)}
            valueFrom={dateFrom}
            valueTo={dateTo}
          />
        </Stack>
      </Grid>
      {loading ? (
        <Stack spacing={2} p={2}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton variant="rectangular" key={i} width="100%" height={30} />
          ))}
        </Stack>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="h4"
                sx={{
                  alignItems: "center",
                }}
              >
                {t("T_dashboard_vendor_1")}
              </Typography>

              <Bar options={options1} data={chartData6New} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <ChartView {...chartData4} onSelectId={handleShowDataCount} />
            </Grid>
            {!data3Change && (
              <Grid item xs={12} sm={6}>
                <ChartView {...chartData3} onSelectId={handleShowDataCount1} />
              </Grid>
            )}

            {data3Change && (
              <Grid item xs={12} sm={6}>
                <ChartView
                  {...chartData3Change}
                  onBack={handleBackButtonClick}
                />{" "}
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <ChartView {...chartData2} onSelectId={handleShowData} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="h4"
                sx={{
                  alignItems: "center",
                }}
              >
                {t("T_dashboard_vendor_5")}
              </Typography>
              <Bar options={options1} data={data} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="h4"
                sx={{
                  alignItems: "center",
                }}
              >
                {t("T_dashboard_vendor_6")}
              </Typography>
              <Bar options={options1} data={chartData} />
            </Grid>
          </Grid>
          {selectedId && (
            <DialogModal
              open={!!selectedId}
              onClose={() => setSelectedId(null)}
              title="Workshop Details"
              fullScreen
              children={
                <List
                  headCells={WorkshopChartsCellsCount1}
                  data={selectedId}
                  getBodyCells={getBodyCells2}
                  feature={feature}
                  canEdit={false}
                />
              }
              actions={<></>}
            />
          )}

          {selectedWorkshop && (
            <DialogModal
              open={!!selectedWorkshop}
              onClose={() => setSelectedWorkshop(null)}
              title="Selected Workshops"
              fullScreen
              children={
                <List
                  headCells={WorkshopChartsCellsCount}
                  data={selectedWorkshop}
                  getBodyCells={getBodyCells1}
                  feature={feature}
                  canEdit={false}
                />
              }
              actions={<></>}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default AnalyticsVendor;
